import { Alert, Button, ErrorState } from '@keboola/design';

import { isNotFoundError } from '../features/auth/errors';

type Props = {
  isError: boolean;
  /** Whether an earlier load left data on screen. */
  hasData: boolean;
  error: unknown;
  onRetry: () => void;
  /** Heading for the blocking state. */
  loadTitle: string;
  /** Alert title for the non-blocking state. */
  refreshTitle: string;
  /** Sentence under that alert. */
  staleNote: string;
  /**
   * Copy for a 404 on a by-id route; the blocking state then drops "Try again".
   * Omitted, a 404 renders as an ordinary load failure.
   */
  notFound?: { title: string; description: string };
  /** Blocking title level; `ErrorState.Title` defaults to `h3`, so a page owning the `h1` passes `h2`. */
  titleAs?: 'h1' | 'h2' | 'h3';
};

/**
 * What a failed query renders.
 *
 * - ⚠️ A failed refetch keeps the previous `data`, so `isError` alone must not block the page.
 * - No data, 404 with `notFound` copy: blocking state, no retry.
 * - No data: blocking `ErrorState`.
 * - Data: non-blocking `Alert` above the stale content.
 */
export function QueryError({
  isError,
  hasData,
  error,
  onRetry,
  loadTitle,
  refreshTitle,
  staleNote,
  notFound,
  titleAs,
}: Props) {
  if (!isError) return null;

  if (!hasData && notFound !== undefined && isNotFoundError(error)) {
    return (
      <ErrorState>
        <ErrorState.Title as={titleAs}>{notFound.title}</ErrorState.Title>
        <ErrorState.Description>{notFound.description}</ErrorState.Description>
      </ErrorState>
    );
  }

  if (!hasData) {
    return (
      <ErrorState>
        <ErrorState.Title as={titleAs}>{loadTitle}</ErrorState.Title>
        {error instanceof Error && <ErrorState.Description>{error.message}</ErrorState.Description>}
        <ErrorState.Actions>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </ErrorState.Actions>
      </ErrorState>
    );
  }

  return (
    <Alert
      variant="error"
      title={refreshTitle}
      actions={
        <Button size="small" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      }
    >
      <p className="tw:m-0">{staleNote}</p>
      {error instanceof Error && <p className="tw:m-0">{error.message}</p>}
    </Alert>
  );
}
